// Shared helpers for rendering assessment + AI report data on the frontend.

export function labelize(s) {
  if (s == null) return "";
  return String(s)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function fmtMoney(amount, currency = "USD") {
  if (amount == null || amount === "") return null;
  const n = typeof amount === "number" ? amount : Number(amount);
  if (Number.isNaN(n)) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${n} ${currency || ""}`.trim();
  }
}

export function joinNonEmpty(parts, sep = " · ") {
  return (parts || []).filter(Boolean).join(sep);
}

export function deriveCandidateSummary(d) {
  if (!d) return "Your assessment is in — we'll build your plan from here.";
  const cs = d.current_situation || {};
  const goals = d.career_goals || {};
  const yearsTotal = (d.work_history || [])
    .map((r) => {
      const s = r.start_year;
      const e = r.end_year || new Date().getFullYear();
      return s ? Math.max(0, e - s) : 0;
    })
    .reduce((a, b) => a + b, 0);
  const topSkills = (d.skills || [])
    .slice(0, 3)
    .map((s) => s.name)
    .filter(Boolean);

  const bits = [];
  if (cs.last_role) bits.push(cs.last_role);
  if (cs.last_industry) bits.push(`in ${cs.last_industry}`);
  if (yearsTotal > 0) bits.push(`with ~${yearsTotal} years of experience`);
  if (cs.employment_status)
    bits.push(`currently ${labelize(cs.employment_status).toLowerCase()}`);

  const sentences = [];
  if (bits.length > 0) sentences.push(bits.join(", ") + ".");
  if (topSkills.length > 0) sentences.push(`Strongest in ${topSkills.join(", ")}.`);
  if (goals.short_term_3_6_months)
    sentences.push(`Short-term goal: ${goals.short_term_3_6_months}`);

  if (sentences.length === 0) {
    sentences.push("Your assessment is in — we'll build your plan from here.");
  }
  return sentences.join(" ");
}

export function deriveFinancialUrgency(fp = {}) {
  const r = fp.runway_months;
  let level = "unknown";
  let copy = "Not enough info yet.";
  if (r != null) {
    if (r < 3) {
      level = "high";
      copy = `Less than 3 months of runway — let's prioritise income first, retraining second.`;
    } else if (r < 6) {
      level = "moderate";
      copy = `${r} months of runway — there's room to be deliberate, but not too patient.`;
    } else {
      level = "low";
      copy = `${r} months of runway — you can afford a more strategic transition.`;
    }
  }
  if (
    fp.has_dependents === true &&
    fp.has_savings_buffer === false &&
    level !== "high"
  ) {
    level = level === "low" ? "moderate" : "high";
    copy += " Dependents + no savings buffer raises the urgency.";
  }
  if (fp.has_debt_stress === true && level === "low") {
    level = "moderate";
    copy += " Debt stress noted.";
  }
  return { level, copy };
}

export function hasReport(assessment) {
  return Boolean(assessment?.ai_recommendation?.pathways);
}

export function aiRiskLevel(s) {
  if (!s) return null;
  const head = String(s).toLowerCase().split(/[^a-z]/).filter(Boolean)[0];
  if (["low", "medium", "high"].includes(head)) return head;
  return null;
}

export const PATHWAY_META = {
  immediate_income: {
    label: "Immediate Income",
    blurb: "Fastest path to cash — bridge work that uses what you already do.",
    accent: "from-sky-50 to-white",
    iconBg: "bg-sky-100 text-sky-700",
    horizon: "0–3 months",
  },
  transitional: {
    label: "Transitional",
    blurb: "Steady stepping stone toward a more durable career.",
    accent: "from-emerald-50 to-white",
    iconBg: "bg-emerald-100 text-emerald-700",
    horizon: "3–12 months",
  },
  long_term_future_proof: {
    label: "Long-term Future-proof",
    blurb: "Where to plant a flag for the next decade.",
    accent: "from-indigo-50 to-white",
    iconBg: "bg-indigo-100 text-indigo-700",
    horizon: "12+ months",
  },
};
